import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import classes from "./RevenuePopover.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { PR_REVENUE_FILTER } from "../../../../../../../redux/actions";
import { performanceRevenueFilterSelector } from "../../../../../../../redux/selectors/performanceDataSelector";
import {
  ADMIN_REVENUE_CATEGORIES,
  CheckboxRevenueOption,
  GENERAL_REVENUE_CATEGORIES,
} from "./RevenuePopover";

interface IRevenuePopoverModalProps {
  id: string;
  open: boolean;
  anchor: HTMLButtonElement;
  onClose: () => void;
}

export const RevenuePopoverModal = ({
  id,
  open,
  anchor,
  onClose,
}: IRevenuePopoverModalProps) => {
  const dispatch = useDispatch();
  const filters = useSelector(performanceRevenueFilterSelector);
  const isAdmin = +(sessionStorage.getItem("ROLE_TYPE") || 1) === 1;

  const [selectAll, setSelectAll] = useState<boolean>(true);
  const [checkboxes, setCheckboxes] = useState<CheckboxRevenueOption[]>([
    ...GENERAL_REVENUE_CATEGORIES,
  ]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newState = event.target.checked;
    setSelectAll(newState);
    setCheckboxes(
      checkboxes.map((checkbox) => ({
        ...checkbox,
        checked:
          checkbox.key === "accommodation_fare_adjusted_usd"
            ? checkbox.checked
            : newState,
      }))
    );
  };

  const handleCheckboxChange =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newState = event.target.checked;
      setCheckboxes(
        checkboxes.map((checkbox, i) =>
          i === index ? { ...checkbox, checked: newState } : checkbox
        )
      );
      setSelectAll(checkboxes.every((checkbox) => checkbox.checked));
    };

  const handleSelectChange =
    (index: number) => (event: SelectChangeEvent<"+" | "-">) => {
      if (checkboxes.filter((el) => el.checked).length > 1) {
        const newValue = event.target.value as "+" | "-";
        setCheckboxes(
          checkboxes.map((checkbox, i) =>
            i === index ? { ...checkbox, value: newValue } : checkbox
          )
        );
      }
    };

  const handleApply = useCallback(() => {
    dispatch({
      type: PR_REVENUE_FILTER,
      payload: {
        revenue_filters: checkboxes,
      },
    });
    onClose();
  }, [checkboxes, dispatch, onClose]);

  const handleReset = useCallback(() => {
    dispatch({
      type: PR_REVENUE_FILTER,
      payload: {
        revenue_filters: null,
      },
    });
    onClose();
  }, [dispatch, onClose]);

  useEffect(() => {
    if (isAdmin) {
      setCheckboxes([
        ...checkboxes.map((el) => ({ ...el, checked: false })),
        ...ADMIN_REVENUE_CATEGORIES,
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (filters) {
      setCheckboxes(filters);
    }
  }, [filters]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-revenue-title"
      aria-describedby="modal-revenue-description"
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        overflowY: "scroll",
      }}
    >
      <Box className={classes.popoverContentWrapper}>
        <h3
          style={{
            marginBottom: "0px",
            fontSize: "24px",
            color: "#02094F",
            margin: 0,
          }}
        >
          Revenue
        </h3>
        <Box color="#5D6293">
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                  indeterminate={
                    !selectAll &&
                    checkboxes.some((checkbox) => checkbox.checked)
                  }
                />
              }
              style={{ fontSize: "14px" }}
              label={
                <Typography fontSize="14px" color="#5D6293">
                  Select all
                </Typography>
              }
            />
            {checkboxes.map((checkbox, index) => (
              <Box
                key={index}
                className={classes.checkboxLine}
                display="flex"
                flexDirection="column"
                gap="8px"
              >
                <FormControlLabel
                  control={
                    <Box mr={1}>
                      <Checkbox
                        checked={checkbox.checked}
                        onChange={handleCheckboxChange(index)}
                        disabled={
                          checkbox.key === "accommodation_fare_adjusted_usd" &&
                          !isAdmin
                        }
                      />
                      <Select
                        value={checkbox.value}
                        onChange={handleSelectChange(index)}
                        style={{
                          color: "#00B48D",
                          height: "30px",
                          width: "66px",
                          fontWeight: 500,
                        }}
                        sx={{
                          "& .MuiSelect-select": {
                            paddingLeft: "20px",
                          },
                        }}
                        variant="outlined"
                        disabled={
                          checkboxes.filter((el) => el.checked).length < 2 ||
                          (checkbox.key === "accommodation_fare_adjusted_usd" &&
                            !isAdmin)
                        }
                      >
                        <MenuItem value="+">+</MenuItem>
                        <MenuItem value="-">-</MenuItem>
                      </Select>
                    </Box>
                  }
                  label={
                    <Typography fontSize="14px" color="#5D6293">
                      {checkbox.label}
                    </Typography>
                  }
                />
                <Divider />
              </Box>
            ))}
          </FormGroup>
        </Box>

        <Box display="flex" gap="16px">
          <Button
            variant="contained"
            className={`${classes.configureButton} ${classes.resetButton}`}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            className={classes.configureButton}
            onClick={handleApply}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
